/**
 * Lecture d'une quantité saisie librement dans le formulaire de contact
 * (« 1,5 M€ », « 50 k », « 1.500.000 », « 1'500'000 », « 2 Md€ », « 15,00,000 »…).
 *
 * Renvoie l'entier exact attendu par le serveur, ou `ok: false` quand la saisie est
 * ambiguë ou non reconnue : on préfère demander une correction que deviner un montant.
 * Calcul en BigInt : « 1,15 M » donne 1150000 et non 1149999,99…
 *
 * @version 1.0.0 - 28/09/2026
 */

export type QuantityResult = { ok: true; value: string } | { ok: false }

export interface QuantityOptions {
    /** Montant : une décimale résiduelle (« 1 500,50 € ») est arrondie. Sinon (quantité dénombrable) elle est refusée. */
    roundDecimals?: boolean
    /** Langue de la page, qui tranche les notations propres à une langue (« billion », « MM », « 1,125 M »). */
    locale?: string
}

const MAX = BigInt(Number.MAX_SAFE_INTEGER)

// Multiplicateurs, comparés au suffixe entier après normalisation (minuscules, sans accent, sans espace ni point).
const MULTIPLIERS: Record<string, bigint> = {}
const define = (value: bigint, words: string[]): void => words.forEach(w => { MULTIPLIERS[w] = value })
define(1_000n, ['k', 'mille', 'milliers', 'mil', 'mila', 'thousand', 'thousands', 'tausend', 'tsd'])
define(100_000n, ['lakh', 'lakhs', 'lac', 'lacs'])
define(1_000_000n, ['m', 'mn', 'mln', 'mio', 'mios', 'million', 'millions', 'millon', 'millones', 'milione', 'milioni',
    'milhao', 'milhoes', 'millionen'])
define(10_000_000n, ['crore', 'crores', 'cr'])
define(1_000_000_000n, ['g', 'md', 'mds', 'mrd', 'mrds', 'mld', 'mlds', 'milliard', 'milliards', 'milliarde', 'milliarden',
    'miliardo', 'miliardi', 'millardo', 'millardos', 'milmillones', 'milmilhoes', 'bilhao', 'bilhoes', 'bn', 'bns', 'b'])

// Notations dont la valeur dépend de la langue : « billion » vaut 10^9 en anglais mais 10^12 en français et en
// espagnol, « MM » vaut un million en anglais mais « mille millions » dans une partie de l'Amérique latine.
// Hors de l'anglais, on les refuse plutôt que de risquer un facteur 1 000.
const ENGLISH_ONLY: Record<string, bigint> = { billion: 1_000_000_000n, billions: 1_000_000_000n, mm: 1_000_000n }

// Séparateur de milliers encore employé devant un multiplicateur, par langue
const THOUSANDS_WITH_MULTIPLIER: Record<string, string> = { en: ',', es: '.' }

const isValidGrouping = (groups: string[]): boolean => {
    const [first, ...rest] = groups
    if (!/^[1-9]\d{0,2}$/.test(first)) return false
    if (rest.every(g => /^\d{3}$/.test(g))) return true
    // Groupement indien : 15,00,000 (dernier groupe de 3, les précédents de 2)
    return first.length <= 2 && rest.length >= 2 && /^\d{3}$/.test(rest[rest.length - 1])
        && rest.slice(0, -1).every(g => /^\d{2}$/.test(g))
}

export const parseQuantity = (raw: string, options: QuantityOptions = {}): QuantityResult => {
    const lang = (options.locale ?? 'fr').slice(0, 2).toLowerCase()
    let s = raw.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()
    if (s === '') return { ok: true, value: '' }

    // Espaces de toute nature (dont insécables et fines) : séparateurs de milliers ou décoratifs
    s = s.replace(/[\s   ]+/g, '')
    // Approximations en tête : la valeur saisie reste la meilleure estimation disponible
    s = s.replace(/^(~|≈|environ|env\.?|approx(imately|\.)?|about|around|circa|aprox(imadamente|\.)?)/, '')
    // Devise euro, avant ou après le nombre (« € 1,5 M », « 1,5 M d'euros », « 2 mil millones de euros »)
    s = s.replace(/(d['’]|de|di)?(euros?|eur|€)/g, '')
    // « K€ » et « M€ » s'écrivent aussi « KE » et « ME »
    s = s.replace(/([km])e$/, '$1')
    // Mentions sans effet sur la valeur : hors taxes, par an
    s = s.replace(/(?:ht|ttc|horstaxes?|\/an|paran|\/year|peryear|\/ano|porano|annuel(?:le)?)+$/, '')
    // Point d'abréviation (« Mio. », « Mrd. », « Tsd. ») et apostrophe suisse (1'500'000)
    s = s.replace(/([a-z])\./g, '$1').replace(/['’`´]/g, '')

    const match = s.match(/^(\d+(?:[.,]\d+)*)([a-z]*)$/)
    if (!match) return { ok: false }
    const [, number, suffix] = match

    let multiplier = 1n
    if (suffix !== '') {
        const found = MULTIPLIERS[suffix] ?? (lang === 'en' ? ENGLISH_ONLY[suffix] : undefined)
        if (found === undefined) return { ok: false }
        multiplier = found
    }

    // Séparateur décimal : le dernier des deux quand les deux sont présents (1.500.000,50 / 1,500,000.50)
    const dots = (number.match(/\./g) ?? []).length
    const commas = (number.match(/,/g) ?? []).length
    let integer = number
    let fraction = ''
    if (dots > 0 && commas > 0) {
        const decimal = number.lastIndexOf('.') > number.lastIndexOf(',') ? '.' : ','
        const thousands = decimal === '.' ? ',' : '.'
        if ((decimal === '.' ? dots : commas) > 1) return { ok: false }
        const [int, frac] = number.split(decimal)
        if (!isValidGrouping(int.split(thousands))) return { ok: false }
        integer = int.split(thousands).join('')
        fraction = frac
    } else if (dots + commas > 1) {
        // Un seul type de séparateur, répété : séparateur de milliers (1.500.000, 1,500,000)
        const groups = number.split(/[.,]/)
        if (!isValidGrouping(groups)) return { ok: false }
        integer = groups.join('')
    } else if (dots + commas === 1) {
        const separator = dots ? '.' : ','
        const [int, frac] = number.split(separator)
        // Séparateur suivi de 3 chiffres : milliers (1,500 / 1.500). Avec un multiplicateur, c'est une décimale
        // (« 1,125 M€ », « 1.125 M€ » tapé au pavé numérique), sauf la notation de presse de la langue :
        // « 1.500 millones » en espagnol, « 1,500 million » en anglais.
        const pressThousands = THOUSANDS_WITH_MULTIPLIER[lang]
        const isThousands = frac.length === 3 && isValidGrouping([int, frac])
            && (multiplier === 1n || separator === pressThousands)
        if (isThousands) integer = int + frac
        else { integer = int; fraction = frac }
    }

    const scale = 10n ** BigInt(fraction.length)
    const scaled = BigInt(integer + fraction) * multiplier
    let result = scaled / scale
    const remainder = scaled % scale
    if (remainder !== 0n) {
        if (!options.roundDecimals) return { ok: false }
        if (remainder * 2n >= scale) result += 1n
    }
    if (result > MAX) return { ok: false }
    return { ok: true, value: result.toString() }
}
